import React, { useState } from "react";
import { toast } from "sonner";
import {
  ExternalLink,
  Link as LinkIcon,
  Code2,
  Eye,
  EyeOff,
  Copy,
  CheckCircle2,
  Star,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { buildIframeCode } from "../lib/drive";
import { PdfPreviewBlock } from "./PdfPreviewBlock";
import { useCategories } from "../context/CategoriesContext";
import { http } from "../lib/api";
import { cn } from "../lib/utils";

function trackClick(pdfId, kind) {
  // Fire-and-forget: never block UX or surface failures to the user.
  http.post(`/pdfs/${pdfId}/click`, { kind }).catch(() => {});
}

function useClipboard() {
  const [copiedKey, setCopiedKey] = useState(null);
  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      toast.error("Unable to copy");
    }
  };
  return { copiedKey, copy };
}

function RevealBlock({ label, value, onCopy, copied, testId }) {
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</span>
        <Button size="sm" variant="outline" onClick={onCopy} className="h-7 px-2 text-xs rounded-lg" data-testid={`${testId}-copy`}>
          {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <code className="block text-xs break-all text-slate-800 font-mono whitespace-pre-wrap rounded-lg bg-white border border-slate-200 px-3 py-2">{value}</code>
    </div>
  );
}

export function PdfItem({ pdf, view, expanded, onToggleExpand, adminActions, compact = false, renderInlinePreview = true }) {
  const [showLink, setShowLink] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const { copiedKey, copy } = useClipboard();

  const { colorForFolder } = useCategories();
  const color = colorForFolder(pdf.folder);
  const iframeCode = buildIframeCode(pdf.embedLink);

  const actionButtons = (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2",
        compact && "justify-center"
      )}
      data-testid={`pdf-actions-${pdf.id}`}
    >
      <Button
        asChild
        size={compact ? "icon" : "sm"}
        className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg"
        data-testid={`open-pdf-${pdf.id}`}
        title={compact ? "Open PDF" : undefined}
        aria-label={compact ? "Open PDF" : undefined}
      >
        <a
          href={pdf.googleDriveLink}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackClick(pdf.id, "open")}
        >
          <ExternalLink className="h-4 w-4" />
          {!compact && "Open PDF"}
        </a>
      </Button>

      <Button
        size={compact ? "icon" : "sm"}
        variant="outline"
        onClick={() => {
          setShowLink((v) => !v);
          if (showEmbed) setShowEmbed(false);
        }}
        data-testid={`show-link-${pdf.id}`}
        title={compact ? (showLink ? "Hide Link" : "Show Link") : undefined}
        aria-label={compact ? (showLink ? "Hide Link" : "Show Link") : undefined}
      >
        <LinkIcon className="h-4 w-4" />
        {!compact && (showLink ? "Hide Link" : "Show Link")}
      </Button>

      <Button
        size={compact ? "icon" : "sm"}
        variant="outline"
        onClick={() => {
          setShowEmbed((v) => !v);
          if (showLink) setShowLink(false);
        }}
        data-testid={`show-embed-${pdf.id}`}
        title={compact ? (showEmbed ? "Hide Embed" : "Show Embed Code") : undefined}
        aria-label={compact ? (showEmbed ? "Hide Embed" : "Show Embed Code") : undefined}
      >
        <Code2 className="h-4 w-4" />
        {!compact && (showEmbed ? "Hide Embed" : "Show Embed Code")}
      </Button>

      <Button
        size={compact ? "icon" : "sm"}
        variant={expanded ? "default" : "outline"}
        onClick={() => {
          if (!expanded) trackClick(pdf.id, "preview");
          onToggleExpand?.();
        }}
        className={cn(expanded && "bg-slate-900 hover:bg-slate-800 text-white")}
        data-testid={`preview-pdf-${pdf.id}`}
        title={compact ? (expanded ? "Close Preview" : "Preview PDF") : undefined}
        aria-label={compact ? (expanded ? "Close Preview" : "Preview PDF") : undefined}
      >
        {expanded ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        {!compact && (expanded ? "Close Preview" : "Preview PDF")}
      </Button>

      {adminActions}
    </div>
  );

  const titleBlock = (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <Badge
          className={cn("rounded-full border-transparent font-semibold", color.bg, color.text)}
          style={color.badgeStyle}
          data-testid={`pdf-folder-${pdf.id}`}
        >
          <span
            className={cn("h-1.5 w-1.5 rounded-full mr-1.5", color.dot)}
            style={color.dotStyle}
          />
          {pdf.folder}
        </Badge>
        {pdf.featured && (
          <Badge
            className="rounded-full bg-brand-accent text-brand-primary border-transparent font-semibold"
            data-testid={`pdf-featured-${pdf.id}`}
          >
            <Star className="h-3 w-3 mr-1 fill-current" />
            Featured
          </Badge>
        )}
      </div>
      <h3
        className="font-display font-bold text-lg sm:text-xl text-slate-900 leading-tight truncate"
        data-testid={`pdf-title-${pdf.id}`}
      >
        {pdf.title}
      </h3>
      {pdf.description && (
        <p className="text-sm text-slate-600 mt-1 leading-relaxed line-clamp-2">{pdf.description}</p>
      )}
    </div>
  );

  const revealedBlocks = (
    <>
      {showLink && (
        <RevealBlock
          label="Google Drive Link"
          value={pdf.googleDriveLink}
          onCopy={() => copy(pdf.googleDriveLink, `link-${pdf.id}`)}
          copied={copiedKey === `link-${pdf.id}`}
          testId={`link-block-${pdf.id}`}
        />
      )}
      {showEmbed && (
        <RevealBlock
          label="Iframe Embed Code"
          value={iframeCode}
          onCopy={() => copy(iframeCode, `embed-${pdf.id}`)}
          copied={copiedKey === `embed-${pdf.id}`}
          testId={`embed-block-${pdf.id}`}
        />
      )}
    </>
  );

  const previewBlock =
    expanded && renderInlinePreview ? (
      <PdfPreviewBlock pdf={pdf} className="mt-4" />
    ) : null;

  if (view === "grid") {
    return (
      <div
        className={cn(
          "rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-brand-accent fade-up",
          pdf.featured && "ring-1 ring-brand-accent/60"
        )}
        data-testid={`pdf-card-${pdf.id}`}
      >
        {titleBlock}
        <div className="mt-4">{actionButtons}</div>
        {revealedBlocks}
        {previewBlock}
      </div>
    );
  }

  // list view
  return (
    <div
      className={cn(
        "rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:border-brand-accent hover:shadow-sm fade-up",
        pdf.featured && "ring-1 ring-brand-accent/60"
      )}
      data-testid={`pdf-row-${pdf.id}`}
    >
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1 min-w-0">{titleBlock}</div>
        <div className="lg:shrink-0">{actionButtons}</div>
      </div>
      {revealedBlocks}
      {previewBlock}
    </div>
  );
}
